/**
 * Auth state — fetches /api/auth/me on mount and exposes the current
 * user (or null if not signed in). Provides sign-out + a refresh hook
 * the Users page calls after mutations.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, loading: true, error: null })

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/me', { credentials: 'include' })
      if (r.status === 401) {
        setState({ user: null, loading: false, error: null })
        return null
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json()
      setState({ user: data.user, loading: false, error: null })
      return data.user
    } catch (e) {
      setState({ user: null, loading: false, error: e })
      return null
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const signOut = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    setState({ user: null, loading: false, error: null })
    window.location.href = '/login'
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * Role helpers derived from the current session. Kept as tiny standalone
 * exports so the Sidebar, AuthGate, and any page can gate a chunk of UI
 * with one import instead of stringly-typed role checks scattered
 * everywhere.
 *
 *   isStaff    = admin OR viewer — the landlord-side operators
 *   isOccupant = the renter (end user of a portfolio)
 *   defaultLandingForRole(user) picks '/portal' vs '/dashboard' for a
 *     signed-in redirect (used by LoginPage post-sign-in and by
 *     AuthGate cross-role bounces).
 */
export function isStaffRole(role)    { return role === 'admin' || role === 'viewer' }
export function isOccupantRole(role) { return role === 'occupant' }

export function defaultLandingForRole(user) {
  if (!user) return '/login'
  if (user.role === 'occupant') return '/portal'
  return '/dashboard'
}

// ── Users API helpers ──────────────────────────────────────────────────

export async function listUsers() {
  const r = await fetch('/api/users', { credentials: 'include' })
  if (!r.ok) throw await asError(r)
  const data = await r.json()
  return data.users
}

export async function inviteUser({ email, role }) {
  const r = await fetch('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, role }),
  })
  if (!r.ok) throw await asError(r)
  return (await r.json()).user
}

export async function setRole(id, role) {
  const r = await fetch(`/api/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ role }),
  })
  if (!r.ok) throw await asError(r)
  return (await r.json()).user
}

export async function removeUser(id) {
  const r = await fetch(`/api/users/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!r.ok) throw await asError(r)
  return true
}

export async function generateMagicLink(id) {
  const r = await fetch(`/api/users/${id}/magic-link`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!r.ok) throw await asError(r)
  return await r.json()  // { url, expires_at, user }
}

export async function generateTempPassword(id, password) {
  const r = await fetch(`/api/users/${id}/temp-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ password: password || undefined }),
  })
  if (!r.ok) throw await asError(r)
  return await r.json()  // { password, user }
}

export async function signInWithPassword(email, password) {
  const r = await fetch('/api/auth/password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  })
  if (!r.ok) throw await asError(r)
  return (await r.json()).user
}

export async function changePassword({ current_password, new_password }) {
  const r = await fetch('/api/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ current_password, new_password }),
  })
  if (!r.ok) throw await asError(r)
  return (await r.json()).user
}

// ── Tenants API helpers ────────────────────────────────────────────────

export async function listTenants() {
  const r = await fetch('/api/tenants', { credentials: 'include' })
  if (!r.ok) throw await asError(r)
  return await r.json()   // { tenants: [...], active_tenant_id }
}

export async function switchTenant(tenantId) {
  const r = await fetch('/api/tenants/switch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ tenant_id: tenantId }),
  })
  if (!r.ok) throw await asError(r)
  return await r.json()
}

export async function createTenant({ slug, display_name }) {
  const r = await fetch('/api/tenants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ slug, display_name }),
  })
  if (!r.ok) throw await asError(r)
  return (await r.json()).tenant
}

export async function addUserToTenant({ tenant_id, user_id, is_default = false }) {
  const r = await fetch(`/api/tenants/${tenant_id}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ user_id, is_default }),
  })
  if (!r.ok) throw await asError(r)
  return await r.json()
}

async function asError(r) {
  let body = null
  try { body = await r.json() } catch {}
  const code = body?.error || `http_${r.status}`
  const message = errorMessage(code)
  const err = new Error(message)
  err.code = code
  err.status = r.status
  return err
}

function errorMessage(code) {
  switch (code) {
    case 'invalid_email':          return 'That doesn\'t look like a valid email.'
    case 'invalid_role':           return 'Role must be admin, viewer, or occupant.'
    case 'already_exists':         return 'A user with that email already exists.'
    case 'occupant_requires_provisioning':
                                   return 'Use the Add Resident flow — occupants must be linked to a property when created.'
    case 'occupant_required':      return 'That page is only for residents.'
    case 'occupant_not_provisioned':
                                   return 'This resident account isn\'t linked to a unit yet — link it first.'
    case 'occupant_cannot_switch_tenant':
                                   return 'Residents can\'t switch portfolios.'
    case 'missing_name':           return 'First name and last name are required.'
    case 'missing_property_id':    return 'Pick a property to link this resident to.'
    case 'property_not_in_tenant': return 'That property isn\'t in the currently-active portfolio.'
    case 'occupant_has_no_email':  return 'Add an email to the resident record before linking a login.'
    case 'already_has_login':      return 'This resident already has a portal login.'
    case 'provisioning_failed':    return 'Something went wrong creating the resident account — check server logs.'
    case 'cannot_demote_self':     return 'You can\'t demote yourself — ask another admin.'
    case 'cannot_delete_self':     return 'You can\'t delete your own account.'
    case 'last_admin':             return 'There must be at least one admin. Promote another user first.'
    case 'not_found':               return 'User not found.'
    case 'auth_required':          return 'You\'re signed out — sign in again.'
    case 'admin_required':         return 'Only admins can do that.'
    case 'bad_credentials':        return 'Email or password is wrong.'
    case 'missing_fields':         return 'Fill in both email and password.'
    case 'password_too_short':     return 'Password must be at least 8 characters.'
    case 'wrong_current_password': return 'Your current password is wrong.'
    case 'invalid_slug':           return 'Slug must be lowercase letters, numbers, or dashes.'
    case 'slug_exists':            return 'A tenant with that slug already exists.'
    case 'missing_display_name':   return 'Tenant needs a display name.'
    case 'invalid_tenant_id':      return 'Invalid tenant id.'
    case 'forbidden':              return 'You don\'t have access to that tenant.'
    case 'no_active_tenant':       return 'No active tenant on this session — try signing out and back in.'
    default:                       return 'Something went wrong.'
  }
}
